import { Button } from "components/DesignSystem/Button/Button";
import { DeleteIcon } from "components/icons/delete";
import { EditPencil } from "components/icons/EditPencil";
import ReactCountryFlag from "react-country-flag";
import { FileInput } from "components/FileInput/FileInput";
import ToolTip from "components/design/toolTip";
import { QuestionMarkCircle } from "components/icons/QuestionMarkCircle";
import { ClampedTextWithTooltip } from "utils/ClampedTextWithTooltip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import classNames from "classnames";
import { formatDate } from "utils/formatDate";

type SavedModeProps = {
  entityName: string;
  suffix?: string;
  countryCode?: string;
  countryName?: string;
  stateName?: string;
  formationTypeName?: string;
  isUSCountry: boolean;
  einFile: File | null;
  coiFile: File | null;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: (file: File) => void;
  isMobile: boolean;
  taxClassification?: string;
  dateOfIncorporation?: string | null;
  businessAddress?: string;
};

export const SavedMode = ({
  entityName,
  suffix,
  countryCode,
  countryName,
  stateName,
  formationTypeName,
  isUSCountry,
  einFile,
  coiFile,
  onEdit,
  onDelete,
  onPreview,
  isMobile,
  taxClassification,
  dateOfIncorporation,
  businessAddress,
}: SavedModeProps) => {
  const entityNameWithSuffix = `${entityName} ${suffix && suffix}`;

  const extraDetails = [
    { label: "Tax classification", value: taxClassification },
    {
      label: "Date of incorporation",
      value: dateOfIncorporation ? formatDate(dateOfIncorporation) : undefined,
    },
    { label: "Business address", value: businessAddress },
  ].filter(({ value }) => Boolean(value));

  return (
    <Accordion.Root
      type="single"
      defaultValue="DETAILS"
      className="t-space-y-5"
      collapsible
    >
      <Accordion.Item value="DETAILS">
        <Accordion.Trigger className="!t-border-b-0 after:!t-border-b-0">
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <div className="t-flex t-items-center t-gap-3">
              {countryCode && (
                <div className="t-w-8 t-h-8 t-rounded-full t-overflow-hidden t-flex-shrink-0">
                  <ReactCountryFlag
                    countryCode={countryCode}
                    svg
                    style={{
                      width: "32px",
                      height: "32px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
              {entityNameWithSuffix && (
                <ClampedTextWithTooltip
                  text={entityNameWithSuffix}
                  lineClampElementClass="t-line-clamp-1 t-text-subtitle t-font-semibold"
                  toolTipClass="t-w-64 t-word-break !t-text-left"
                />
              )}
            </div>
            {!isMobile && (
              <div className="t-flex t-items-center t-gap-2">
                <Button
                  type="button"
                  customType="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  size="small"
                >
                  <DeleteIcon />
                  <span className="t-ml-1">Delete</span>
                </Button>
                <Button
                  type="button"
                  customType="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  size="small"
                >
                  <EditPencil />
                  <span className="t-ml-1">Edit</span>
                </Button>
              </div>
            )}
          </div>
        </Accordion.Trigger>
        <Accordion.Content>
          <div
            className={classNames("t-pb-6", {
              "t-grid t-grid-cols-2 t-gap-6": countryCode === "US",
            })}
          >
            <div>
              <div className="t-text-caption t-text-text-30 t-mb-1">
                Country
              </div>
              <div className="t-text-body-sm">{countryName}</div>
            </div>
            <div>
              {countryCode === "US" && (
                <>
                  <div className="t-text-caption t-text-text-30 t-mb-1">
                    State
                  </div>
                  <div className="t-text-body-sm">{stateName}</div>
                </>
              )}
            </div>
          </div>
          <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-6">
            <div>
              <div className="t-text-caption t-text-text-30 t-mb-1">
                Formation
              </div>
              <div className="t-text-body-sm">{formationTypeName}</div>
            </div>
            <div>
              <div className="t-text-caption t-text-text-30 t-mb-1">
                Entity name
              </div>
              <div className="t-text-body-sm">
                {entityName} {suffix && suffix}
              </div>
            </div>
          </div>

          {extraDetails.length > 0 && (
            <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-6 t-mt-6">
              {extraDetails.map(({ label, value }) => (
                <div key={label}>
                  <div className="t-text-caption t-text-text-30 t-mb-1">
                    {label}
                  </div>
                  <div className="t-text-body-sm">{value}</div>
                </div>
              ))}
            </div>
          )}

          {isUSCountry && einFile && (
            <div className="t-mt-6">
              <FileInput
                label={
                  <div className="t-flex t-justify-between t-items-center">
                    <div>EIN letter</div>
                    <ToolTip
                      text={
                        <div className="t-text-center t-w-72">
                          Your COI is the legal proof of your company's
                          formation. It confirms your official name, entity
                          type, and state of incorporation. We use it to
                          automatically set up your U.S. entity without manual
                          entry.
                        </div>
                      }
                    >
                      <div className="t-flex t-items-center">
                        <QuestionMarkCircle className="t-text-neutral-40" />
                        <div className="t-text-caption t-italic t-text-neutral-40">
                          Why do we need this?
                        </div>
                      </div>
                    </ToolTip>
                  </div>
                }
                withForm={false}
                file={einFile}
                onFileClick={() => onPreview(einFile)}
              />
            </div>
          )}

          {coiFile && (
            <div className="t-mt-6">
              <FileInput
                label="Certificate of Incorporation"
                withForm={false}
                file={coiFile}
                onFileClick={() => onPreview(coiFile)}
              />
            </div>
          )}

          {isMobile && (
            <div className="t-flex t-items-center t-gap-2 t-mt-2">
              <Button
                type="button"
                customType="ghost"
                onClick={onDelete}
                size="small"
              >
                <DeleteIcon />
                <span className="t-ml-1">Delete</span>
              </Button>
              <Button
                type="button"
                customType="ghost"
                onClick={onEdit}
                size="small"
              >
                <EditPencil />
                <span className="t-ml-1">Edit</span>
              </Button>
            </div>
          )}
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
};
